import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import api from "../../api/axios";
import { useSnackbar } from "../../utils/SnackbarProvider";

export default function VendorStorePolicy() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [policies, setPolicies] = useState({
    returnPolicy: "",
    shippingPolicy: "",
    warranty: "",
    processingTime: "1_day",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/vendor/policies");

        setPolicies({
          returnPolicy: data.policies?.returnPolicy || "",
          shippingPolicy: data.policies?.shippingPolicy || "",
          warranty: data.policies?.warranty || "",
          processingTime: data.policies?.processingTime || "1_day",
        });
      } catch (err) {
        console.error("Failed to fetch policies", err);
        showSnackbar("Failed to load policies", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.patch("/vendor/policies", policies);

      showSnackbar("Policies saved successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to save policies", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f0f2f5" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>📋 Store Policies</Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Manage return, shipping, warranty, and processing policies for your store.</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
        {/* ================= Return Policy ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Return Policy
          </Typography>
        </Box>
        <Paper sx={{ p: 3, borderRadius: '12px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={policies.returnPolicy}
            onChange={(e) =>
              setPolicies({ ...policies, returnPolicy: e.target.value })
            }
            placeholder="e.g. 7-day return available. Product must be unused."
            variant="outlined"
          />
        </Paper>

        {/* ================= Shipping Policy ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Shipping Policy
          </Typography>
        </Box>
        <Paper sx={{ p: 3, borderRadius: '12px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={policies.shippingPolicy}
            onChange={(e) =>
              setPolicies({ ...policies, shippingPolicy: e.target.value })
            }
            placeholder="e.g. Ships within 1-2 days"
            variant="outlined"
          />
        </Paper>

        {/* ================= Warranty ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Warranty
          </Typography>
        </Box>
        <Paper sx={{ p: 3, borderRadius: '12px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', mb: 3 }}>
          <TextField
            fullWidth
            value={policies.warranty}
            onChange={(e) =>
              setPolicies({ ...policies, warranty: e.target.value })
            }
            placeholder="e.g. 1 year manufacturer warranty"
            variant="outlined"
          />
        </Paper>

        {/* ================= Processing Time ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Processing Time
          </Typography>
        </Box>
        <Paper sx={{ p: 3, borderRadius: '12px', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', mb: 3 }}>
          <Select
            fullWidth
            value={policies.processingTime}
            onChange={(e) =>
              setPolicies({ ...policies, processingTime: e.target.value })
            }
            variant="outlined"
          >
            <MenuItem value="same_day">Same Day</MenuItem>
            <MenuItem value="1_day">1 Day</MenuItem>
            <MenuItem value="2_days">2 Days</MenuItem>
            <MenuItem value="3_5_days">3-5 Days</MenuItem>
            <MenuItem value="1_week">1 Week</MenuItem>
          </Select>
        </Paper>

        {/* ================= Save Button ================= */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              px: 4,
              py: 1,
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: "#ffa41c",
              color: "#111",
              "&:hover": { bgcolor: "#f90" },
              "&:disabled": { bgcolor: "#ccc" },
            }}
          >
            {saving ? "Saving..." : "Save Policies"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}